import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, of } from 'rxjs';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { StorageService } from '../storage/storage.service';
import { environment } from 'src/environments/environment';
import { TranslocoService } from '@jsverse/transloco';

// TODO: vedere utilizzo interceptors per cache

@Injectable({
  providedIn: 'root',
})
export class ApiService {

  constructor(private http: HttpClient, private storageService: StorageService) {}

  login(username: string, password: string, admin: boolean = false): Observable<any> {
    return this.http.post(environment.apiUrl + (admin ? '/admin/auth/login' : '/auth/login'), { username, password });
  }

  getModels(): Observable<any> {
    return this.http.get(environment.apiUrl + '/models');
  }

  getFineTuningModels(company: string, api_key: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/models/fine-tuning', {
      params: {
        company: company,
      },
      headers: {
        'api-key': api_key
      }
    });
  }

  sendResetPasswordRequest(username: string): Observable<any> {
    return this.http.post(environment.apiUrl + '/auth/request-reset-password', { username });
  }

  sendResetPassword(token: string, password: string): Observable<any> {
    return this.http.post(environment.apiUrl + '/auth/reset-password/' + token, password);
  }

  getChatBots(): Observable<any> {
    return this.http.get(environment.apiUrl + '/assistant');
  }

  getUsers(): Observable<any> {
    return this.http.get(environment.apiUrl + '/admin/user');
  }

  createAssistant(data: any): Observable<any> {
    return this.http.post(environment.apiUrl + '/assistant', data);
  }

  getChatBot(assistant: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/assistant/' + assistant);
  }

  getDashboard(assistant: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/assistant/' + assistant + '/dashboard');
  }

  getKnownladge(assistant: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/assistant/' + assistant + '/knowledge');
  }

  uploadDocument(assistant: string, formData: FormData): Observable<any> {
    return this.http.post(environment.apiUrl + '/assistant/' + assistant + '/file', formData);
  }

  deleteDocument(assistant: string, uuid: string): Observable<any> {
    return this.http.delete(environment.apiUrl + '/assistant/' + assistant + '/document/' + uuid);
  }

  addUrl(assistant: string, url: string[]): Observable<any> {
    return this.http.post(environment.apiUrl + '/assistant/' + assistant + '/url', url);
  }

  saveAssistant(assistant: string, data: any): Observable<any> {
    return this.http.put(environment.apiUrl + '/assistant/' + assistant, data);
  }

  deleteAssistant(assistant: string): Observable<any> {
    return this.http.delete(environment.apiUrl + '/assistant/' + assistant);
  }

  connectToDrive(assistant: string, code: string): Observable<any> {
    return this.http.post(environment.apiUrl + '/assistant/' + assistant + '/drive', { code });
  }

  deleteDriveFolder(assistant: string): Observable<any> {
    return this.http.delete(environment.apiUrl + '/assistant/' + assistant + '/drive');
  }

  getChats(assistant: string, page: number = 1): Observable<any> {
    return this.http.get(environment.apiUrl + '/assistant/' + assistant + '/chat?page=' + page);
  }

  getMessages(assistant: string, chat: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/assistant/' + assistant + '/chat/' + chat + '/message');
  }

  createGoogleDriveFile(assistant: string, fileType: string, fileName: string | undefined = undefined): Observable<any> {
    return this.http.put(environment.apiUrl + '/assistant/' + assistant + '/drive/file', { fileType, fileName });
  }

  refreshGoogleDriveFile(assistant: string): Observable<any> {
    return this.http.post(environment.apiUrl + '/assistant/' + assistant + '/drive/refresh', {});
  }

  addQuestion(assistant: string, question: string, answer: string, message_uuid: string | null = null): Observable<any> {
    return this.http.post(environment.apiUrl + '/assistant/' + assistant + '/question', { question, answer, message_uuid });
  }

  adminCreateUser(user: any): Observable<any> {
    return this.http.post(environment.apiUrl + '/admin/user', user);
  }

  adminDeleteUser(uuid: string): Observable<any> {
    return this.http.delete(environment.apiUrl + '/admin/user/' + uuid);
  }

  adminUpdateUser(uuid: string, user: any): Observable<any> {
    return this.http.put(environment.apiUrl + '/admin/user/' + uuid, user);
  }

  getUserInfo(): Observable<any> {
    return this.http.get(environment.apiUrl + '/user/profile');
  }

  updateUserInfo(data: any): Observable<any> {
    return this.http.put(environment.apiUrl + '/user/profile', data);
  }

  requestDeleteAccount(): Observable<any> {
    return this.http.post(environment.apiUrl + '/user/delete-account', {});
  }

  adminGetTools(): Observable<any> {
    return this.http.get(environment.apiUrl + '/admin/tool', {
      headers: {
        'Authorization': 'Bearer ' + this.storageService.get('user')?.token
      }
    });
  }

  getTools(assistant: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/assistant/' + assistant + '/tool');
  }

  activeTool(assistant: string, tool: string): Observable<any> {
    return this.http.post(environment.apiUrl + '/assistant/' + assistant + '/tool/' + tool + '/activate', {});
  }

  adminUpdateAssistant(assistant: string, data: any): Observable<any> {
    return this.http.put(environment.apiUrl + '/admin/assistant/' + assistant, data);
  }

  getDriveFileTypes(assistant: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/assistant/' + assistant + '/drive/file-types');
  }

  addLeadForm(assistant: string, data: any): Observable<any> {
    return this.http.post(environment.apiUrl + '/assistant/' + assistant + '/lead-form', data);
  }

  deleteLeadForm(assistant: string, uuid: string): Observable<any> {
    return this.http.delete(environment.apiUrl + '/assistant/' + assistant + '/lead-form/' + uuid);
  }

  updateLeadForm(assistant: string, uuid: string, data: any): Observable<any> {
    return this.http.put(environment.apiUrl + '/assistant/' + assistant + '/lead-form/' + uuid, data);
  }

  getLeads(assistant: string, page: number = 1): Observable<any> {
    return this.http.get(environment.apiUrl + '/assistant/' + assistant + '/lead?page=' + page);
  }

  exportLeadCSV(assistant: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/assistant/' + assistant + '/lead/export', {
      responseType: 'arraybuffer',
    });
  }

  getTranslate(assistant: string, source_lang: string, text: string, target_lang: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/assistant/' + assistant + '/translate', {
      params: { source_lang, text, target_lang }
    });
  }
}
